import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { store } from '../app/store';
import { refreshTokenThunk } from '../features/auth/authThunks';
import { clearAuth } from '../features/auth/authSlice';

// ===== API CONFIGURATION =====

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // Increased timeout for debugging
  headers: {
    'Content-Type': 'application/json',
  },
});

console.log('🔧 API configured with base URL:', API_BASE_URL);

// ===== REQUEST INTERCEPTOR =====

api.interceptors.request.use(
  (config) => {
    // Get current auth state
    const state = store.getState();
    const accessToken = state.auth.accessToken;
    
    // Add auth token to requests
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    
    // Log request in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`🚀 API Request: ${config.method?.toUpperCase()} ${config.url}`);
    }
    
    return config;
  },
  (error) => {
    console.error('❌ Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// ===== RESPONSE INTERCEPTOR =====

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  
  failedQueue = [];
};

api.interceptors.response.use(
  (response: AxiosResponse) => {
    // Log successful response in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`✅ API Response: ${response.config.method?.toUpperCase()} ${response.config.url} - ${response.status}`);
    }
    
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

    // Log error in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`❌ API Error: ${originalRequest?.method?.toUpperCase()} ${originalRequest?.url} - ${error.response?.status}`);
      console.log('Error details:', {
        message: error.message,
        code: error.code,
        status: error.response?.status,
        data: error.response?.data
      });
    }
    
    // Handle 401 errors (token expired)
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => {
          return api(originalRequest);
        }).catch((err) => {
          return Promise.reject(err);
        });
      }
      
      originalRequest._retry = true;
      isRefreshing = true;
      
      try {
        // Attempt to refresh token
        const result = await store.dispatch(refreshTokenThunk());
        
        if (refreshTokenThunk.fulfilled.match(result)) {
          // Token refreshed successfully
          const newToken = result.payload.access_token;
          processQueue(null, newToken);
          
          // Retry original request with new token
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
          }
          
          return api(originalRequest);
        } else {
          // Refresh failed
          processQueue(error, null);
          store.dispatch(clearAuth());
          return Promise.reject(error);
        }
      } catch (refreshError) {
        // Refresh failed
        processQueue(refreshError, null);
        store.dispatch(clearAuth());
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    
    return Promise.reject(error);
  }
);

// ===== API METHODS =====

export const apiClient = {
  // Generic methods
  get: <T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> =>
    api.get(url, config),
  
  post: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> =>
    api.post(url, data, config),
  
  put: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> =>
    api.put(url, data, config),
  
  patch: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> =>
    api.patch(url, data, config),
  
  delete: <T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> =>
    api.delete(url, config),
  
  // Health check
  healthCheck: async (): Promise<boolean> => {
    try {
      const response = await api.get('/health');
      return response.status === 200;
    } catch (error) {
      console.warn('API health check failed:', error);
      return false;
    }
  },
};

// ===== AUTH API METHODS =====

export const authApi = {
  // Login
  login: async (email: string, password: string) => {
    console.log('🔍 Making login request to:', `${API_BASE_URL}/auth/login`);
    console.log('🔍 Login payload:', { email, password: '***' });

    const response = await api.post('/auth/login', { email, password });

    console.log('🔍 Login response status:', response.status);
    console.log('🔍 Login response data:', response.data);

    return response.data;
  },
  
  // Register
  register: async (userData: {
    name: string;
    email: string;
    password: string;
    confirmPassword: string;
    role?: string;
  }) => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },
  
  // Refresh token
  refreshToken: async (refreshToken: string) => {
    const response = await api.post('/auth/refresh', { refresh_token: refreshToken });
    return response.data;
  },
  
  // Logout
  logout: async () => {
    try {
      console.log('🚪 API: Sending logout request...');
      const response = await api.post('/auth/logout', {}, {
        timeout: 5000 // 5 second timeout for logout
      });
      console.log('✅ API: Logout response received');
      return response.data;
    } catch (error) {
      console.warn('⚠️ API: Logout request failed:', error);
      throw error;
    }
  },
  
  // Logout all
  logoutAll: async () => {
    const response = await api.post('/auth/logout-all');
    return response.data;
  },
  
  // Get profile
  getProfile: async () => {
    const response = await api.get('/auth/profile');
    return response.data;
  },
  
  // Update profile
  updateProfile: async (profileData: any) => {
    const response = await api.put('/auth/profile', profileData);
    return response.data;
  },
  
  // Change password
  changePassword: async (passwordData: {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
  }) => {
    const response = await api.put('/auth/change-password', passwordData);
    return response.data;
  },
  
  // Get active sessions
  getActiveSessions: async () => {
    const response = await api.get('/auth/sessions');
    return response.data;
  },
};

// ===== ACCOUNT API METHODS =====

export const accountApi = {
  // Create account
  createAccount: async (accountData: {
    name: string;
    description?: string;
    planId?: string;
    ownerData: {
      name: string;
      email: string;
      password: string;
    };
  }) => {
    const response = await api.post('/accounts', accountData);
    return response.data;
  },
  
  // Get accounts
  getAccounts: async () => {
    const response = await api.get('/accounts');
    return response.data;
  },
  
  // Get account by ID
  getAccount: async (id: string) => {
    const response = await api.get(`/accounts/${id}`);
    return response.data;
  },
  
  // Update account
  updateAccount: async (id: string, accountData: any) => {
    const response = await api.put(`/accounts/${id}`, accountData);
    return response.data;
  },
};

// ===== USER API METHODS =====

export const userApi = {
  // Get users
  getUsers: async () => {
    const response = await api.get('/users');
    return response.data;
  },
  
  // Get user by ID
  getUser: async (id: string) => {
    const response = await api.get(`/users/${id}`);
    return response.data;
  },
  
  // Create user
  createUser: async (userData: any) => {
    const response = await api.post('/users', userData);
    return response.data;
  },
  
  // Update user
  updateUser: async (id: string, userData: any) => {
    const response = await api.put(`/users/${id}`, userData);
    return response.data;
  },
  
  // Delete user
  deleteUser: async (id: string) => {
    const response = await api.delete(`/users/${id}`);
    return response.data;
  },
};

// Export the main api instance for direct use if needed
export default api;
